import { z, type ZodError } from "zod";

const originSchema = z
  .string()
  .min(1)
  .refine(
    (val) => {
      try {
        return new URL(val).origin === val;
      } catch {
        return false;
      }
    },
    { message: "Must be a valid URL origin (scheme + host[, port])" },
  );

const urlSchema = z
  .string()
  .min(1)
  .refine(
    (val) => {
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: "Must be a valid URL" },
  );

export type PublicRuntimeConfig = {
  statisticFetchingIntervalSeconds: number;
  consoleServiceOrigin: string;
  cognitoAuthority: string;
  cognitoClientId: string;
  cognitoScope: string;
  cognitoResponseType: string;
};

export const publicRuntimeConfigResponseSchema = z.object({
  statisticFetchingIntervalSeconds: z.number().int().positive(),
  consoleServiceOrigin: originSchema,
  cognitoAuthority: urlSchema,
  cognitoClientId: z.string().min(1),
  cognitoScope: z.string().min(1),
  cognitoResponseType: z.string().min(1),
});

const formatPublicRuntimeConfigZod = (e: ZodError): string =>
  e.issues.map((x) => `${x.path.join(".")}: ${x.message}`).join("; ");

export const parsePublicRuntimeConfigJson = (
  data: unknown,
): PublicRuntimeConfig => {
  const r = publicRuntimeConfigResponseSchema.safeParse(data);
  if (r.success) {
    return r.data;
  }
  throw new Error(
    `GET /config returned invalid JSON shape: ${formatPublicRuntimeConfigZod(r.error)}`,
    { cause: r.error },
  );
};
